import React, { useEffect, useState } from "react";
import {
  BarChart3,
  BookOpenCheck,
  CalendarDays,
  ClipboardCheck,
  Clock3,
  LineChart,
  Target,
} from "lucide-react";
import Headerdash from "./head";
import Sidebar from "./sidebar";
import PieCharts from "../bars/pie";
import ExamDateCard from "./DashboardComponent/ExamDateCard";
import DesiredScoreCard from "./DashboardComponent/DesiredScoreCard";

const zoneStyles = {
  Speaking: {
    panel: "border-green-200 bg-green-50",
    icon: "bg-green-100 text-green-700",
    text: "text-green-700",
    progress: "bg-green-400",
  },
  Writing: {
    panel: "border-blue-200 bg-blue-50",
    icon: "bg-blue-100 text-blue-700",
    text: "text-blue-700",
    progress: "bg-blue-400",
  },
  Reading: {
    panel: "border-orange-200 bg-orange-50",
    icon: "bg-orange-100 text-orange-700",
    text: "text-orange-700",
    progress: "bg-orange-400",
  },
  Listening: {
    panel: "border-purple-200 bg-purple-50",
    icon: "bg-purple-100 text-purple-700",
    text: "text-purple-700",
    progress: "bg-purple-400",
  },
};

const getProgress = (attempted, total) => {
  if (!total) return 0;
  return Math.min(100, Math.round((attempted / total) * 100));
};

const DashboardShell = ({ children }) => (
  <div className="min-h-screen bg-slate-50 text-gray-700">
    <Headerdash />
    <Sidebar />
    <main className="min-h-screen pt-20 sm:pl-72">
      <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {children}
      </div>
    </main>
  </div>
);

const SummaryCard = ({ icon: Icon, label, value, helper }) => (
  <div className="min-h-[144px] rounded-md border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-purple-200 hover:shadow-md">
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-sm font-semibold uppercase text-gray-500">
          {label}
        </p>
        <p className="mt-4 text-2xl font-extrabold text-gray-800">{value}</p>
      </div>
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-slate-100 text-purple-700">
        <Icon className="h-5 w-5" aria-hidden="true" />
      </span>
    </div>
    {helper && <p className="mt-3 text-sm text-gray-500">{helper}</p>}
  </div>
);

const DashboardLoading = () => (
  <section className="rounded-md border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
    <div className="flex flex-col gap-4 border-b border-slate-100 pb-5 md:flex-row md:items-center md:justify-between">
      <div>
        <div className="h-4 w-24 rounded-sm bg-fuchsia-100" />
        <div className="mt-3 h-8 w-64 max-w-full rounded-sm bg-slate-200" />
      </div>
      <div className="flex w-full items-center gap-3 rounded-md bg-slate-50 p-3 md:w-56">
        <div className="h-10 w-10 shrink-0 rounded-md bg-white shadow-sm" />
        <div className="flex-1">
          <div className="h-3 w-24 rounded-sm bg-slate-200" />
          <div className="mt-2 h-6 w-16 rounded-sm bg-slate-200" />
        </div>
      </div>
    </div>

    <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(260px,360px),1fr]">
      <div className="rounded-md bg-slate-50 p-5">
        <div className="mx-auto aspect-square max-w-[280px] rounded-full border-[18px] border-fuchsia-100 bg-white" />
        <div className="mt-5 grid grid-cols-2 gap-3">
          {[1, 2, 3, 4].map((item) => (
            <div key={item} className="rounded-md bg-white p-3">
              <div className="h-4 w-20 rounded-sm bg-slate-200" />
              <div className="mt-2 h-3 w-24 rounded-sm bg-slate-100" />
            </div>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {[1, 2, 3, 4, 5, 6, 7].map((item) => (
          <div
            key={item}
            className="min-h-[144px] rounded-md border border-slate-200 bg-white p-5 shadow-sm"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="h-4 w-28 rounded-sm bg-slate-200" />
                <div className="mt-4 h-7 w-16 rounded-sm bg-slate-200" />
                <div className="mt-3 h-4 w-36 rounded-sm bg-slate-100" />
              </div>
              <div className="h-10 w-10 rounded-md bg-fuchsia-50" />
            </div>
          </div>
        ))}
      </div>
    </div>

    <div className="mt-6 flex items-center gap-3 text-sm font-medium text-gray-500">
      <span className="relative flex h-2.5 w-2.5">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-fuchsia-400 opacity-75" />
        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-fuchsia-700" />
      </span>
      Loading your dashboard
    </div>
  </section>
);

const ZoneCard = ({ name, attempted, total, averageScore }) => {
  const styles = zoneStyles[name];
  const progress = getProgress(attempted, total);

  return (
    <article
      className={`rounded-md border p-5 shadow-sm ${styles.panel}`}
      aria-label={`${name} practice progress`}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className={`text-lg font-bold ${styles.text}`}>{name}</p>
          <p className="mt-1 text-sm text-gray-500">Practice progress</p>
        </div>
        <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-md ${styles.icon}`}>
          <BookOpenCheck className="h-5 w-5" aria-hidden="true" />
        </span>
      </div>

      <div className="mt-6 grid grid-cols-3 gap-3 text-sm">
        <div className="rounded-md bg-white/80 p-3">
          <p className="text-gray-500">Done</p>
          <p className="mt-1 text-xl font-extrabold text-gray-800">
            {attempted}
          </p>
        </div>
        <div className="rounded-md bg-white/80 p-3">
          <p className="text-gray-500">Total</p>
          <p className="mt-1 text-xl font-extrabold text-gray-800">{total}</p>
        </div>
        <div className="rounded-md bg-white/80 p-3">
          <p className="text-gray-500">Average</p>
          <p className="mt-1 text-xl font-extrabold text-gray-800">
            {averageScore}
          </p>
        </div>
      </div>

      <div className="mt-5">
        <div className="mb-2 flex items-center justify-between text-xs font-semibold text-gray-500">
          <span>Completion</span>
          <span>{progress}%</span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-white">
          <div
            className={`h-full rounded-full ${styles.progress}`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </article>
  );
};

export default function Dashboard() {
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const response = await fetch("/api/dashboard", {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        });

        if (!response.ok) {
          throw new Error("Failed to fetch dashboard data");
        }

        const data = await response.json();
        setDashboardData(data);
      } catch (fetchError) {
        console.error("Error fetching dashboard data:", fetchError);
        setError(fetchError.message || "Unable to load dashboard data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  useEffect(() => {
    const checkSubscription = async () => {
      try {
        const response = await fetch("/api/check-subscription-cron", {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        });

        if (!response.ok) {
          throw new Error("Failed to check subscription status");
        }

        await response.json();
      } catch (subscriptionError) {
        console.error("Error checking subscription status:", subscriptionError);
      }
    };

    checkSubscription();
  }, []);

  if (isLoading) {
    return (
      <DashboardShell>
        <DashboardLoading />
      </DashboardShell>
    );
  }

  if (error) {
    return (
      <DashboardShell>
        <div className="rounded-md border border-red-100 bg-white p-6 shadow-sm">
          <p className="text-sm font-semibold uppercase text-red-500">
            Dashboard unavailable
          </p>
          <h1 className="mt-2 text-2xl font-bold text-gray-900">
            We could not load your dashboard.
          </h1>
          <p className="mt-2 text-gray-600">{error}</p>
        </div>
      </DashboardShell>
    );
  }

  const totalQuestionsSpeakingZone =
    dashboardData["Speaking Zone"]?.totalQuestions || 0;
  const totalQuestionsWritingZone =
    dashboardData["Writing Zone"]?.totalQuestions || 0;
  const totalQuestionsReadingZone =
    dashboardData["Reading Zone"]?.totalQuestions || 0;
  const totalQuestionsListeningZone =
    dashboardData["Listening Zone"]?.totalQuestions || 0;

  const totalAttemptedQuestionsSpeakingZone =
    dashboardData["Speaking Zone"]?.attemptedQuestions || 0;
  const totalAttemptedQuestionsWritingZone =
    dashboardData["Writing Zone"]?.attemptedQuestions || 0;
  const totalAttemptedQuestionsReadingZone =
    dashboardData["Reading Zone"]?.attemptedQuestions || 0;
  const totalAttemptedQuestionsListeningZone =
    dashboardData["Listening Zone"]?.attemptedQuestions || 0;

  const totalQuestionPracticed =
    totalAttemptedQuestionsSpeakingZone +
    totalAttemptedQuestionsWritingZone +
    totalAttemptedQuestionsReadingZone +
    totalAttemptedQuestionsListeningZone;

  const summaryCards = [
    {
      icon: ClipboardCheck,
      label: "Mock Practiced",
      value: "0",
      helper: "Completed mock exams",
    },
    {
      icon: Clock3,
      label: "Days Left",
      value:
        dashboardData?.daysLeft != null ? `${dashboardData.daysLeft} days` : "N/A",
      helper: "Until your exam date",
    },
    {
      icon: BarChart3,
      label: "Question Practiced",
      value: totalQuestionPracticed,
      helper: "Across all practice zones",
    },
    {
      icon: LineChart,
      label: "Daily Practice",
      value: "N/A",
      helper: "Coming from recent activity",
    },
    {
      icon: CalendarDays,
      label: "Weekly Prediction",
      value: "N/A",
      helper: "Based on weekly attempts",
    },
  ];

  const zoneCards = [
    {
      name: "Speaking",
      attempted: totalAttemptedQuestionsSpeakingZone,
      total: totalQuestionsSpeakingZone,
      averageScore: 30,
    },
    {
      name: "Writing",
      attempted: totalAttemptedQuestionsWritingZone,
      total: totalQuestionsWritingZone,
      averageScore: 30,
    },
    {
      name: "Reading",
      attempted: totalAttemptedQuestionsReadingZone,
      total: totalQuestionsReadingZone,
      averageScore: 30,
    },
    {
      name: "Listening",
      attempted: totalAttemptedQuestionsListeningZone,
      total: totalQuestionsListeningZone,
      averageScore: 30,
    },
  ];

  return (
    <DashboardShell>
      <section className="rounded-md border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
        <div className="flex flex-col gap-4 border-b border-slate-100 pb-5 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase text-purple-700">
              Dashboard
            </p>
            <h1 className="mt-1 text-2xl font-extrabold text-gray-900 sm:text-3xl">
              Estimated Score in Exams
            </h1>
          </div>
          <div className="flex w-full items-center gap-3 rounded-md bg-slate-50 p-3 md:w-auto">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-white text-purple-700 shadow-sm">
              <Target className="h-5 w-5" aria-hidden="true" />
            </span>
            <div>
              <p className="text-xs font-semibold uppercase text-gray-500">
                Current estimate
              </p>
              <p className="text-xl font-extrabold text-gray-900">N/A</p>
            </div>
          </div>
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(260px,360px),1fr]">
          <div className="rounded-md bg-slate-50 p-5">
            <div className="relative mx-auto aspect-square max-w-[280px]">
              <PieCharts />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="rounded-full bg-white px-4 py-3 text-3xl font-extrabold text-gray-700 shadow-sm">
                  N/A
                </span>
              </div>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
              {zoneCards.map((zone) => (
                <div key={zone.name} className="rounded-md bg-white p-3">
                  <p className="font-semibold text-gray-700">{zone.name}</p>
                  <p className="mt-1 text-gray-500">
                    {getProgress(zone.attempted, zone.total)}% complete
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            <SummaryCard {...summaryCards[0]} />
            <DesiredScoreCard desiredScore={dashboardData?.desiredScore} />
            <ExamDateCard examDate={dashboardData?.examDate} />
            {summaryCards.slice(1).map((card) => (
              <SummaryCard key={card.label} {...card} />
            ))}
          </div>
        </div>
      </section>

      <section className="mt-6">
        <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase text-purple-700">
              Practice Result
            </p>
            <h2 className="text-2xl font-extrabold text-gray-900">
              Zone performance
            </h2>
          </div>
          <p className="text-sm text-gray-500">
            Done, total questions, and average score by section.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {zoneCards.map((zone) => (
            <ZoneCard key={zone.name} {...zone} />
          ))}
        </div>
      </section>
    </DashboardShell>
  );
}
